import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import InlineTextInput from '@/components/ui/inline-text-input';
import { useCreateAuctionMutation } from '@/hooks/use-create-auction-mutation';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

type CreateTradeAuctionProps = {
  initialName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (auctionId: string) => void;
};

const CreateTradeAuctionDialog = ({
  initialName,
  open,
  onOpenChange,
  onSuccess,
}: CreateTradeAuctionProps) => {
  const [name, setName] = useState(initialName || '');
  const { createAuctionMutation } = useCreateAuctionMutation({
    onSuccess: (auction) => {
      onOpenChange(false);
      setName('');
      if (onSuccess) {
        onSuccess(auction.id);
      }
    },
  });

  const handleCreate = () => {
    if (!name.trim()) return;

    createAuctionMutation.mutate({
      id: uuidv4(),
      name: name.trim(),
    });
  };

  useEffect(() => {
    // If the dialog is open, set the name to the initial name
    if (open) {
      setName(initialName || '');
    }
  }, [open, initialName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        className="sm:max-w-[425px]"
        onClickClose={() => onOpenChange(false)}
      >
        <DialogHeader>
          <DialogTitle>Create New Auction</DialogTitle>
          <DialogDescription>
            Add a new auction option for trades.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <InlineTextInput
            className="rounded text-xs"
            placeholder="Auction name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) {
                handleCreate();
              }
            }}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleCreate}
            disabled={!name.trim() || createAuctionMutation.isPending}
          >
            {createAuctionMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTradeAuctionDialog;
